import { css } from "lit";
import { baseStyles } from "../../../theme/theme";

export const purchaseCheckoutStyles = [
  baseStyles,
  css`
    :host {
      display: block;
      background: var(--zui-bg);
    }
    .page {
      max-width: 1080px;
      margin: 0 auto;
      padding: 1.75rem 1.5rem 3.5rem;
    }
    .grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr) 360px;
      gap: 1.5rem;
      align-items: start;
    }
    @media (max-width: 860px) {
      .grid {
        /* minmax(0,…) not bare 1fr — bare 1fr's auto floor is the summary
             card's min-content and overflows narrow viewports. */
        grid-template-columns: minmax(0, 1fr);
      }
      /* X2: once the grid stacks into one column, the summary aside would sit
           BELOW the steps (incl. Payment). Reorder the two grid children so the
           Order Summary renders above the steps on mobile. */
      .col {
        order: 1;
      }
      .summary {
        order: 0;
      }
    }
    .col {
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
      min-width: 0;
    }
    .back {
      appearance: none;
      background: none;
      border: 0;
      color: var(--zui-accent);
      cursor: pointer;
      font: inherit;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 0;
      align-self: flex-start;
      margin-bottom: 0.25rem;
    }
    .card {
      background: #fff;
      border: 1px solid var(--zui-line);
      border-radius: 14px;
    }
    .step {
      padding: 1.125rem 1.25rem;
      transition:
        box-shadow 0.15s ease,
        border-color 0.15s ease;
    }
    .step.open {
      border-color: var(--zui-accent);
      box-shadow: 0 0 0 3px var(--zui-accent-soft);
    }
    .step-head {
      display: flex;
      align-items: center;
      /* 'flex-wrap' rather than a width breakpoint: this embed is a custom
           element sized by whatever container the host page drops it in, so a
           viewport media query cannot tell how much room the row actually has.
           Wrapping drops the sign-in action onto its own line exactly when the
           step title no longer fits beside it, at any embed width. */
      flex-wrap: wrap;
      gap: 0.5rem 0.7rem;
      cursor: pointer;
    }
    /* 7/25 QA: the sign-in affordance is a real (secondary) button on the
         step headline row, not a link trailing the microcopy under it. Prompt +
         button travel together so they wrap as one unit. */
    .signin-cta {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.5rem 0.75rem;
      margin-left: auto;
      cursor: auto;
    }
    .signin-cta-t {
      font-size: 13px;
      color: var(--zui-panel-ink-soft);
    }
    .num {
      width: 26px;
      height: 26px;
      border-radius: 999px;
      display: grid;
      place-items: center;
      font-size: 0.8rem;
      font-weight: 700;
      /* Neutralize host-page line-height so the number glyph sits dead-center
           in the circle instead of riding high (equal top/bottom). See PR2/X3. */
      line-height: 1;
      background: transparent;
      color: var(--zui-panel-ink-soft);
      border: 1px solid var(--zui-line);
      flex: 0 0 auto;
    }
    .num.open {
      background: var(--zui-accent);
      color: var(--zui-btn-text);
      border-color: var(--zui-accent);
    }
    .num.done {
      background: #3a7a4d;
      color: #fff;
      border-color: #3a7a4d;
    }
    .step-title {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 16px;
      color: var(--zui-headline);
    }
    .edit {
      margin-left: auto;
      appearance: none;
      background: none;
      border: 0;
      color: var(--zui-accent);
      font: inherit;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .step-sum {
      font-size: 13px;
      color: var(--zui-panel-ink-soft);
      margin: 0.3rem 0 0 2.1rem;
    }
    .body {
      margin: 0.85rem 0 0 2.1rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    @media (max-width: 860px) {
      .body {
        margin-left: 0;
      }
    }
    label.fld {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--zui-panel-ink-soft);
      /* PR1: let the field shrink below the input's intrinsic width so the
           2-up rows (Last name / Phone, and the Family/Guest recipient
           fields) don't overflow their column on narrow viewports. */
      min-width: 0;
    }
    .grid2 > * {
      min-width: 0;
    }
    input,
    textarea,
    select.fld-select {
      padding: 0.6rem 0.7rem;
      border: 1.5px solid var(--zui-line);
      border-radius: 12px;
      background: var(--zui-panel);
      color: var(--zui-headline);
      font: inherit;
      font-size: 15px;
    }
    /* PR1: constrain the text-like fields to their grid cell (mirrors the
         booking checkout). Scoped to the typed inputs so the consent/terms
         checkboxes keep their native size. */
    input[type="text"],
    input[type="email"],
    input[type="tel"],
    input[type="date"],
    textarea,
    select.fld-select {
      width: 100%;
      min-width: 0;
    }
    input:focus,
    textarea:focus,
    select.fld-select:focus {
      outline: none;
      border-color: var(--zui-accent);
      box-shadow: 0 0 0 3px var(--zui-accent-soft);
    }
    .grid2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.6rem;
    }
    @media (max-width: 540px) {
      .grid2 {
        grid-template-columns: 1fr;
      }
    }
    .note {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      background: var(--zui-accent-soft);
      color: var(--zui-panel-ink);
      border-radius: 8px;
      padding: 0.6rem 0.7rem;
      font-size: 12px;
      line-height: 1.4;
    }
    .consent {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      font-size: 11.5px;
      color: var(--zui-panel-ink-soft);
      line-height: 1.4;
    }
    .waiver-fields {
      display: flex;
      flex-direction: column;
      gap: 0.7rem;
      margin-top: 0.75rem;
    }
    div.fld {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--zui-panel-ink-soft);
      min-width: 0;
    }
    .signup-age-group { min-width: 0; }
    .signup-age-group p { margin: 0.4rem 0 0; line-height: 1.5; font-size: 0.8rem; }
    .signup-age-help { color: var(--zui-panel-ink-soft); }
    label.fld input { min-height: 44px; box-sizing: border-box; }
    .fld-req {
      color: #b4541f;
      margin-left: 0.15rem;
    }
    .fld-err {
      color: #b4541f;
      font-size: 0.72rem;
      font-weight: 500;
      line-height: 1.3;
      margin-top: 0.2rem;
    }
    label.fld.invalid input,
    label.fld.invalid textarea,
    label.fld.invalid select.fld-select {
      border-color: #b4541f;
    }
    .opt-chips {
      display: flex;
      flex-wrap: wrap;
      gap: 0.4rem;
      margin-top: 0.1rem;
    }
    .opt-chip {
      appearance: none;
      border: 1.5px solid var(--zui-line);
      border-radius: 999px;
      background: var(--zui-panel);
      color: var(--zui-headline);
      cursor: pointer;
      font: inherit;
      font-size: 13px;
      font-weight: 500;
      padding: 0.35rem 0.75rem;
    }
    .opt-chip[aria-pressed="true"] {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .opt-chips.invalid .opt-chip {
      border-color: #b4541f;
    }
    input.sig-input {
      border-style: dashed;
      font-family: cursive, serif;
      font-size: 17px;
      font-style: italic;
    }
    .sig-hint {
      color: var(--zui-panel-ink-soft);
      font-size: 0.7rem;
      font-weight: 500;
      margin-top: 0.15rem;
    }
    label.fld.referral {
      display: none;
    }
    .others {
      border: 1.5px dashed var(--zui-line);
      border-radius: 14px;
      padding: 1rem;
    }
    .others.on {
      border-color: var(--zui-accent);
      background: var(--zui-accent-soft);
    }
    .others-toggle {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
      appearance: none;
      background: none;
      border: 0;
      cursor: pointer;
      text-align: left;
      font: inherit;
      padding: 0;
      /* X4: title + subtitle are inline siblings that wrap into one
           paragraph; the line-box strut inherits the host page's line-height,
           so it read very tall (img-100). Pin the strut tight. */
      line-height: 1.3;
    }
    .others-toggle .ic {
      width: 36px;
      height: 36px;
      border-radius: 999px;
      background: #fff;
      color: var(--zui-accent-deep);
      display: grid;
      place-items: center;
      flex: 0 0 auto;
      border: 1px solid var(--zui-line);
    }
    .others-toggle .t {
      font-weight: 700;
      font-size: 15px;
      color: var(--zui-headline);
    }
    .others-toggle .s {
      font-size: 12.5px;
      color: var(--zui-panel-ink-soft);
      margin-top: 1px;
    }
    .switch {
      margin-left: auto;
      flex: 0 0 auto;
      width: 38px;
      height: 22px;
      border-radius: 999px;
      background: var(--zui-line);
      position: relative;
      transition: background 0.15s ease;
    }
    .switch.on {
      background: var(--zui-accent);
    }
    .switch::after {
      content: "";
      position: absolute;
      top: 2px;
      left: 2px;
      width: 18px;
      height: 18px;
      border-radius: 999px;
      background: #fff;
      transition: transform 0.15s ease;
    }
    .switch.on::after {
      transform: translateX(16px);
    }
    .scenario-row {
      display: flex;
      gap: 1.25rem;
      flex-wrap: wrap;
      margin-top: 0.75rem;
    }
    .fm-list {
      margin: 0.75rem 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }
    .fm-opt {
      appearance: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      border: 1px solid var(--zui-line);
      border-radius: 10px;
      background: #fff;
      font: inherit;
      font-size: 0.95rem;
      color: var(--zui-headline);
      padding: 0.7rem 0.75rem;
      cursor: pointer;
    }
    .fm-opt.sel {
      border-color: var(--zui-accent);
      color: var(--zui-accent-deep, var(--zui-accent));
      font-weight: 700;
    }
    .fm-empty {
      font-size: 0.85rem;
      color: var(--zui-panel-ink-soft);
      padding: 0.25rem 0;
    }
    .fm-manual {
      margin-top: 0.5rem;
    }
    .fm-manual-t {
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--zui-panel-ink-soft);
      margin: 0 0 0.5rem;
    }
    /* Recipient options are mutually exclusive, so they are real radios in a
         radiogroup. appearance:none keeps the 16px accent-filled control the
         checkbox version had — only the shape changes (square tick → dot), so
         the affordance finally matches the one-of-N behaviour. */
    .radio-box {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      font: inherit;
      font-size: 0.9rem;
      color: var(--zui-headline);
      cursor: pointer;
    }
    .radio-box input[type="radio"] {
      appearance: none;
      -webkit-appearance: none;
      flex: none;
      width: 16px;
      height: 16px;
      margin: 0;
      padding: 0;
      border-radius: 50%;
      border: 1.5px solid var(--zui-panel-ink-soft);
      background: transparent;
      display: grid;
      place-items: center;
      cursor: pointer;
    }
    .radio-box input[type="radio"]::after {
      content: "";
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: transparent;
    }
    .radio-box input[type="radio"]:checked {
      background: var(--zui-accent);
      border-color: var(--zui-accent);
    }
    .radio-box input[type="radio"]:checked::after {
      background: #fff;
    }
    .recipient {
      border: 1px solid var(--zui-line);
      border-radius: 12px;
      background: #fff;
      padding: 0.85rem;
      margin-top: 0.6rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .recipient h5 {
      margin: 0;
      font-size: 0.72rem;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
    }
    .recipient .help {
      font-size: 11.5px;
      color: var(--zui-panel-ink-soft);
      line-height: 1.4;
    }
    .waiver {
      border: 1px solid var(--zui-line);
      border-radius: 12px;
      overflow: hidden;
      background: #fff;
    }
    .waiver-head {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      width: 100%;
      appearance: none;
      background: none;
      border: 0;
      text-align: left;
      cursor: pointer;
      padding: 0.75rem 0.875rem;
      font: inherit;
    }
    .waiver-head .ic {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      background: var(--zui-accent-soft);
      color: var(--zui-accent-deep);
      display: grid;
      place-items: center;
      flex: 0 0 auto;
    }
    .waiver-head .t {
      font-size: 13.5px;
      font-weight: 700;
      color: var(--zui-headline);
      /* PR-WAIVER: pin the title/subline line boxes tight so the host page's
           line-height doesn't open a big gap between the waiver title and the
           "tap to review" subline (mirrors EV-WAIVER). */
      line-height: 1.25;
    }
    .waiver-head .s {
      font-size: 11.5px;
      color: var(--zui-panel-ink-soft);
      line-height: 1.25;
    }
    .waiver-head .chev {
      margin-left: auto;
      color: var(--zui-panel-ink-soft);
      transition: transform 0.2s ease;
    }
    .waiver-head .chev.open {
      transform: rotate(180deg);
    }
    .waiver-body {
      padding: 0 0.875rem 0.875rem;
      font-size: 13px;
      /* PR-WAIVER: compact the agreement text (was 1.55, read too airy)
           while keeping it readable (mirrors EV-WAIVER). */
      line-height: 1.4;
      color: var(--zui-panel-ink-soft);
    }
    .waiver-head + .waiver-body {
      border-top: 1px solid var(--zui-line);
      padding-top: 0.875rem;
    }
    .waiver-body > p {
      margin: 0 0 0.65rem;
    }
    .waiver-body > p:last-of-type {
      margin-bottom: 0;
    }
    .waiver-check {
      display: flex;
      /* PR-WAIVER: center the consent text against its checkbox so the two
           sit aligned rather than the text riding high off the box top
           (mirrors EV-WAIVER). */
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 0.875rem;
      border-top: 1px solid var(--zui-line);
      font-size: 12.5px;
      line-height: 1.4;
      color: var(--zui-headline);
    }
    .cta {
      appearance: none;
      cursor: pointer;
      border: 0;
      background: var(--zui-accent);
      color: var(--zui-btn-text);
      font-family: var(--zui-font);
      font-weight: 700;
      border-radius: var(--zui-btn-radius, 10px);
      padding: 0.8rem 1.5rem;
      min-height: 44px;
    }
    .cta.block {
      width: 100%;
    }
    .cta.right {
      align-self: flex-end;
    }
    .cta:disabled {
      background: #d8d6cf;
      cursor: default;
    }
    /* order summary */
    .summary {
      position: sticky;
      top: 1.75rem;
      padding: 1.25rem;
    }
    @media (max-width: 860px) {
      .summary {
        position: static;
      }
    }
    .summary h3 {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 18px;
      letter-spacing: -0.01em;
      color: var(--zui-headline);
      margin: 0 0 1rem;
    }
    .sum-item {
      display: flex;
      gap: 0.75rem;
      align-items: flex-start;
    }
    .sum-item .thumb {
      width: 56px;
      height: 56px;
      border-radius: 10px;
      overflow: hidden;
      flex: 0 0 auto;
      background: var(--zui-accent-soft);
    }
    .sum-item .thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .sum-item .nm {
      font-weight: 700;
      font-size: 0.9rem;
      color: var(--zui-headline);
      /* X4: product titles that wrap (e.g. "Gold Member (+ Guest Pass)",
           img-101) inherit the host page's line-height and read too tall. */
      line-height: 1.25;
    }
    .sum-item .mt {
      font-size: 0.75rem;
      color: var(--zui-panel-ink-soft);
      line-height: 1.25;
    }
    .sum-item .pr {
      margin-left: auto;
      font-weight: 700;
      color: var(--zui-headline);
    }
    .features {
      margin: 0.875rem 0;
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .feature {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.83rem;
      color: var(--zui-panel-ink);
    }
    .feature .fc {
      color: var(--zui-accent-deep);
      flex: 0 0 auto;
    }
    .promo .lbl {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
      margin-bottom: 0.5rem;
    }
    .promo .row {
      display: flex;
      gap: 0.5rem;
    }
    .promo input {
      flex: 1;
      border-radius: 10px;
      background: #f4f2ec;
      min-width: 0;
    }
    .promo button {
      appearance: none;
      border: 1px solid var(--zui-line);
      background: #fff;
      color: var(--zui-headline);
      border-radius: var(--zui-btn-radius, 10px);
      padding: 0 0.9rem;
      font: inherit;
      font-weight: 700;
      font-size: 0.78rem;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
    }
    .promo button:disabled {
      opacity: 0.6;
      cursor: default;
    }
    .promo-msg {
      margin-top: 0.4rem;
      font-size: 0.75rem;
    }
    .promo-msg.err {
      color: #b4541f;
    }
    .promo-msg.ok {
      color: #3a7a4d;
      font-weight: 600;
    }
    .totals {
      margin-top: 0.875rem;
    }
    /* Qualifies the total directly above it: the tax and fee lines are absent
       because nothing has priced them yet, not because they are zero. */
    .totals-note {
      margin: 0.4rem 0 0;
      font-size: 0.78rem;
      color: var(--zui-muted, #6b7a70);
    }
    .li {
      display: flex;
      justify-content: space-between;
      font-size: 0.9rem;
      padding: 0.2rem 0;
      color: var(--zui-panel-ink);
    }
    .total {
      display: flex;
      justify-content: space-between;
      font-family: var(--zui-font);
      font-weight: 800;
      font-size: 1.05rem;
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid var(--zui-line);
      color: var(--zui-headline);
    }
    .ssl {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      margin-top: 1rem;
      padding-top: 0.875rem;
      border-top: 1px solid var(--zui-line);
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
    }
  `,
];
